<template>
  <div class="ventas-form">
    <main class="main-content">

      <!-- Header grid: Cliente + Documento -->
      <div class="header-grid">
        <!-- Cliente -->
        <section class="form-section">
          <h3 class="section-title">CLIENTE</h3>
          <div class="search-wrapper">
            <div class="input-row">
              <button class="input-icon-btn" @click="buscarCliente" title="Buscar">
                <span class="material-symbols-outlined">search</span>
              </button>
              <input
                v-model="form.customer"
                @keyup.enter="buscarCliente"
                class="form-input"
                placeholder="Buscar Cliente (RUC/Cédula/Nombre)..."
                type="text"
              />
            </div>
            <div v-if="showClientList && searchResults.length > 0" class="dropdown-list">
              <div
                v-for="client in searchResults"
                :key="client.client_id"
                class="dropdown-item"
                @click="selectClient(client)"
              >
                <div class="dropdown-main">{{ client.client_nombre }}</div>
                <div class="dropdown-sub">{{ client.client_rucCed }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Detalles del documento -->
        <section class="form-section">
          <h3 class="section-title">DOCUMENTO</h3>
          <div class="details-row">
            <div class="select-wrapper">
              <select v-model="form.documentType" class="form-select">
                <option value="invoice">Factura</option>
                <option value="receipt">Recibo</option>
                <option value="quote">Cotización</option>
              </select>
              <span class="material-symbols-outlined select-icon">expand_more</span>
            </div>
            <input v-model="form.sequence" class="form-input input-readonly" readonly type="text"/>
          </div>
        </section>
      </div>

      <hr class="divider"/>

      <!-- Artículos -->
      <section class="form-section">
        <div class="section-header">
          <h3 class="section-title">ARTÍCULOS</h3>
        </div>
        <div class="search-wrapper">
          <div class="input-row">
            <button class="input-icon-btn" @click="buscarArticulos" title="Buscar artículo">
              <span class="material-symbols-outlined">add_shopping_cart</span>
            </button>
            <input
              v-model="itemSearch"
              @keyup.enter="buscarArticulos"
              class="form-input"
              placeholder="Buscar Artículos o Servicios..."
              type="text"
            />
            <button class="input-icon-btn" title="Escanear código">
              <span class="material-symbols-outlined" style="color: var(--color-primary)">barcode_scanner</span>
            </button>
          </div>
          <div v-if="showItemList && searchResultsItems.length > 0" class="dropdown-list">
            <div
              v-for="(item, index) in searchResultsItems"
              :key="index"
              class="dropdown-item"
              @click="selectItem(item)"
            >
              <div class="dropdown-main">{{ item.name }}</div>
              <div class="dropdown-sub">Precio: {{ formatCurrency(item.price) }}</div>
            </div>
          </div>
        </div>

        <div class="items-list" v-if="form.items.length > 0">
          <div v-for="(item, index) in form.items" :key="item.id" class="item-card">
            <button class="remove-badge" @click="removeItem(index)">
              <span class="material-symbols-outlined">close</span>
            </button>
            <div class="item-thumb"></div>
            <div class="item-info">
              <p class="item-name">{{ item.name }}</p>
              <p class="item-cat">{{ item.category }}</p>
            </div>
            <div class="item-price-block">
              <p class="item-price">{{ formatCurrency(item.price) }}</p>
              <button class="item-qty" @click="openQuantityModal(index)">x {{ item.quantity }}</button>
            </div>
          </div>
        </div>

        <button class="btn-add-membership" @click="openMembershipModal">
          <span class="material-symbols-outlined">card_membership</span>
          Agregar Membresía
        </button>
      </section>

      <hr class="divider"/>

      <!-- Método de Pago -->
      <section class="form-section">
        <h3 class="section-title">MÉTODO DE PAGO</h3>
        <div class="payment-tabs">
          <button
            v-for="method in paymentMethods"
            :key="method.value"
            :class="['payment-tab', form.paymentMethod === method.value ? 'payment-tab-active' : '']"
            @click="setPaymentMethod(method.value)"
          >{{ method.label }}</button>
        </div>

        <!-- Contado -->
        <div v-if="form.paymentMethod === 'contado'" class="mt">
          <div class="items-list mb" v-if="form.payments.length > 0">
            <div v-for="(pay, index) in form.payments" :key="index" class="item-card compact-card">
              <span class="material-symbols-outlined" style="color: var(--color-primary); flex-shrink:0">
                {{ getPaymentIcon(pay.type) }}
              </span>
              <div class="item-info">
                <p class="item-name" style="font-size: 0.9rem">{{ getPaymentLabel(pay.type) }}</p>
                <p class="item-cat" v-if="pay.reference">Ref: {{ pay.reference }}</p>
              </div>
              <div class="item-price-block">
                <p class="item-price" style="font-size: 0.9rem">{{ formatCurrency(pay.amount) }}</p>
              </div>
              <button class="btn-icon-sm" @click="removePayment(index)">
                <span class="material-symbols-outlined" style="color: var(--color-error)">delete</span>
              </button>
            </div>
          </div>
          <button class="btn-add-membership" @click="openPaymentModal">
            <span class="material-symbols-outlined">add_card</span>
            Agregar Forma de Pago
          </button>
        </div>

        <!-- Crédito -->
        <div v-else class="mt">
          <div class="info-box mb">
            <span class="material-symbols-outlined" style="color: var(--color-info); flex-shrink:0">pending_actions</span>
            <p style="margin:0; font-size: 0.85rem; color: var(--color-text-secondary)">
              Se generará una cuenta por cobrar (CXC). Para pago mixto use el módulo de Abonos.
            </p>
          </div>
          <div class="details-row">
            <div class="form-field">
              <label class="field-label">Monto Crédito</label>
              <input v-model.number="form.creditAmount" type="number" class="form-input px"/>
            </div>
            <div class="form-field">
              <label class="field-label">Días Crédito</label>
              <input v-model.number="form.creditDays" type="number" class="form-input px"/>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer grid: Info + Totales -->
      <div class="footer-grid">
        <!-- Info + Observaciones -->
        <section class="form-section">
          <div class="info-cards-row">
            <div class="info-card">
              <p class="info-card-label">Caja Asignada</p>
              <div class="info-card-content">
                <span class="material-symbols-outlined" style="color: var(--color-primary)">point_of_sale</span>
                <select
                  v-if="activeBoxes.length > 0"
                  v-model="assignedBox"
                  class="info-select"
                >
                  <option v-for="box in activeBoxes" :key="box.contCaja_ID" :value="box.contCaja_caja">
                    {{ box.contCaja_caja }}
                  </option>
                </select>
                <p v-else class="info-card-value">{{ assignedBox }}</p>
              </div>
            </div>
            <div class="info-card">
              <p class="info-card-label">Vendedor</p>
              <div class="info-card-content">
                <span class="material-symbols-outlined" style="color: var(--color-primary)">badge</span>
                <p class="info-card-value">{{ form.seller }}</p>
              </div>
            </div>
          </div>
          <h3 class="section-title">OBSERVACIONES</h3>
          <textarea
            v-model="form.observations"
            class="form-textarea"
            placeholder="Notas sobre entrega o pago..."
          ></textarea>
        </section>

        <!-- Totales -->
        <section class="totals-box">
          <div class="total-row">
            <span class="total-label">Subtotal Gravado</span>
            <span class="total-value">{{ formatCurrency(subtotalGravado) }}</span>
          </div>
          <div class="total-row">
            <span class="total-label">Subtotal No Gravado</span>
            <span class="total-value">{{ formatCurrency(subtotalNoGravado) }}</span>
          </div>
          <div class="total-row">
            <span class="total-label">
              Descuento (<input
                v-model.number="discountPercentage"
                type="number"
                min="0"
                max="100"
                class="discount-input"
              />%)
            </span>
            <span class="total-value">- {{ formatCurrency(descuento) }}</span>
          </div>
          <div class="total-row">
            <span class="total-label">IVA ({{ ivaRate }}%)</span>
            <span class="total-value">{{ formatCurrency(tax) }}</span>
          </div>
          <div class="total-divider"></div>
          <div class="total-row total-final">
            <span>Total</span>
            <span class="total-amount">{{ formatCurrency(total) }}</span>
          </div>
        </section>
      </div>

      <!-- Acciones -->
      <div class="actions-bar">
        <button class="btn-icon-action" @click="handlePrint" title="Imprimir">
          <span class="material-symbols-outlined">print</span>
        </button>
        <button class="btn-cancel" @click="handleCancel">Cancelar</button>
        <button class="btn-save" @click="handleSave" :disabled="savingDoc">
          <span class="material-symbols-outlined">save</span>
          {{ savingDoc ? 'Guardando...' : 'Guardar Documento' }}
        </button>
      </div>

    </main>

    <!-- Modal: Agregar Forma de Pago -->
    <v-dialog v-model="showPaymentModal" max-width="400px">
      <v-card>
        <v-card-title style="font-size: 1.1rem; font-weight: 600; color: var(--color-text-primary)">
          Agregar Pago
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="modal-field">
            <label class="field-label">Tipo</label>
            <div class="select-wrapper">
              <select v-model="currentPayment.type" class="form-select">
                <option v-for="t in paymentTypesList" :key="t.value" :value="t.value">{{ t.text }}</option>
              </select>
              <span class="material-symbols-outlined select-icon">expand_more</span>
            </div>
          </div>
          <div class="modal-field mt">
            <label class="field-label">Monto</label>
            <input v-model.number="currentPayment.amount" type="number" class="form-input px"/>
          </div>
          <div class="modal-field mt">
            <label class="field-label">Referencia (Opcional)</label>
            <input v-model="currentPayment.reference" type="text" class="form-input px" placeholder="Nº Cheque, Lote, etc."/>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <button class="btn-cancel" @click="showPaymentModal = false">Cancelar</button>
          <button class="btn-save" style="flex: 0 0 auto; padding: 0.5rem 1.5rem" @click="addPayment">Agregar</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Editar Cantidad -->
    <v-dialog v-model="showQuantityModal" max-width="300px">
      <v-card>
        <v-card-title style="font-size: 1.1rem; font-weight: 600; color: var(--color-text-primary)">
          Editar Cantidad
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="modal-field">
            <label class="field-label">Nueva Cantidad</label>
            <input v-model.number="editingItem.quantity" type="number" class="form-input px" @keyup.enter="updateQuantity"/>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <button class="btn-cancel" @click="closeQuantityModal">Cancelar</button>
          <button class="btn-save" style="flex: 0 0 auto; padding: 0.5rem 1.5rem" @click="updateQuantity">Guardar</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: Seleccionar Membresía -->
    <v-dialog v-model="showMembershipModal" max-width="500px">
      <v-card>
        <v-card-title style="font-size: 1.1rem; font-weight: 600; color: var(--color-text-primary)">
          Seleccionar Membresía
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="loadingMemberships" style="text-align: center; color: var(--color-text-secondary)">
            Cargando membresías...
          </div>
          <div v-else class="membership-list">
            <div
              v-for="m in availableMemberships"
              :key="m.id_tipo_membresia"
              class="dropdown-item"
              @click="selectMembership(m)"
            >
              <div class="dropdown-main" style="display: flex; align-items: center; gap: 0.5rem">
                <span v-if="clientMembershipIds.includes(m.id_tipo_membresia)"
                      class="material-symbols-outlined"
                      style="color: var(--color-primary); font-size: 1rem">star</span>
                {{ m.nombre_tipo }}
              </div>
              <div class="dropdown-sub">{{ formatCurrency(m.valor) }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <button class="btn-cancel" @click="showMembershipModal = false">Cerrar</button>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ApiService from '../../Servicios/ApiService'

export default {
  name: 'SalesDocumentForm',
  data () {
    const currentSeller = localStorage.getItem('nombreUsuario') || 'Usuario'
    return {
      itemSearch: '',
      activeBoxes: [],
      assignedBox: '',
      showPaymentModal: false,
      showQuantityModal: false,
      showMembershipModal: false,
      loadingMemberships: false,
      savingDoc: false,
      availableMemberships: [],
      clientMembershipIds: [],
      editingItem: { quantity: 1 },
      editingItemIndex: null,
      showClientList: false,
      showItemList: false,
      searchResults: [],
      searchResultsItems: [],
      selectedClient: null,
      form: {
        customer: '',
        documentType: 'invoice',
        sequence: 'SEQ-2025-001',
        paymentMethod: 'contado',
        creditAmount: 0,
        creditDays: 30,
        payments: [],
        seller: currentSeller,
        observations: '',
        items: []
      },
      paymentMethods: [
        { value: 'contado', label: 'Contado' },
        { value: 'credito', label: 'Crédito' }
      ],
      paymentTypesList: [
        { text: 'Efectivo',      value: 'cash',     icon: 'payments'          },
        { text: 'Transferencia', value: 'transfer', icon: 'account_balance'   },
        { text: 'Tarjeta',       value: 'card',     icon: 'credit_card'       },
        { text: 'Cheque',        value: 'check',    icon: 'confirmation_number'},
        { text: 'Depósito',      value: 'deposit',  icon: 'savings'           }
      ],
      currentPayment: { type: 'cash', amount: 0, reference: '' },
      discountPercentage: 0,
      ivaRate: 15
    }
  },
  computed: {
    subtotalGravado () {
      return this.form.items
        .filter(i => i.has_vat)
        .reduce((s, i) => s + i.price * i.quantity, 0)
    },
    subtotalNoGravado () {
      return this.form.items
        .filter(i => !i.has_vat)
        .reduce((s, i) => s + i.price * i.quantity, 0)
    },
    subtotal () { return this.subtotalGravado + this.subtotalNoGravado },
    descuento () { return this.subtotal * ((this.discountPercentage || 0) / 100) },
    tax ()      { return this.subtotalGravado * (this.ivaRate / 100) },
    total ()    { return this.subtotal - this.descuento + this.tax }
  },
  mounted () {
    this.fetchActiveBoxes()
  },
  watch: {
    'currentPayment.type' (val) {
      this.currentPayment.reference = val === 'cash' ? this.assignedBox : ''
    }
  },
  methods: {
    formatCurrency (amount) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
    },
    async fetchActiveBoxes () {
      try {
        const data = await ApiService.get('api/panel/contabilidad/cajas_activas')
        if (Array.isArray(data)) {
          this.activeBoxes = data
          if (data.length > 0) this.assignedBox = data[0].contCaja_caja
        }
      } catch (err) {
        console.error('Error al obtener cajas activas:', err)
      }
    },
    async buscarCliente () {
      if (!this.form.customer) return
      try {
        const data = await ApiService.buscarClientes(this.form.customer)
        if (data.length > 0) {
          this.searchResults = data
          this.showClientList = true
        } else {
          alert('Cliente no encontrado')
          this.searchResults = []
        }
      } catch (err) {
        console.error('Error al buscar cliente:', err)
      }
    },
    selectClient (client) {
      this.selectedClient = client
      this.form.customer = client.client_nombre
      this.showClientList = false
      this.searchResults = []
    },
    async buscarArticulos () {
      if (!this.itemSearch) return
      try {
        const data = await ApiService.get('api/items/productos', { buscar: this.itemSearch })
        const items = Array.isArray(data) ? data : (data.items || data.data || [])
        if (items.length > 0) {
          this.searchResultsItems = items.map(i => ({
            id:       i.codigo,
            name:     i.nombre,
            category: i.categoria || 'General',
            price:    parseFloat(i.precio_unitario || 0),
            image:    '',
            has_vat:  (i.tarifa_iva || 0) > 0
          }))
          this.showItemList = true
        } else {
          this.searchResultsItems = []
          this.showItemList = false
        }
      } catch (err) {
        console.error('Error al buscar artículos:', err)
      }
    },
    selectItem (item) {
      this.form.items.push({
        id:       item.id || Date.now(),
        name:     item.name,
        category: item.category,
        price:    item.price,
        quantity: 1,
        image:    '',
        has_vat:  item.has_vat
      })
      this.itemSearch = ''
      this.showItemList = false
      this.searchResultsItems = []
    },
    removeItem (index) { this.form.items.splice(index, 1) },
    setPaymentMethod (value) {
      this.form.paymentMethod = value
      if (value === 'credito') {
        this.form.creditAmount = this.total
        this.form.creditDays = 30
      }
    },
    openPaymentModal () {
      const paid = this.form.payments.reduce((s, p) => s + p.amount, 0)
      this.currentPayment = {
        type:      'cash',
        amount:    Math.max(0, this.total - paid),
        reference: this.assignedBox
      }
      this.showPaymentModal = true
    },
    addPayment () {
      if (this.currentPayment.amount > 0) {
        this.form.payments.push({ ...this.currentPayment })
        this.showPaymentModal = false
      }
    },
    removePayment (index) { this.form.payments.splice(index, 1) },
    getPaymentIcon (type) {
      const t = this.paymentTypesList.find(t => t.value === type)
      return t ? t.icon : 'payments'
    },
    getPaymentLabel (type) {
      const t = this.paymentTypesList.find(t => t.value === type)
      return t ? t.text : type
    },
    openQuantityModal (index) {
      this.editingItemIndex = index
      this.editingItem = { ...this.form.items[index] }
      this.showQuantityModal = true
    },
    updateQuantity () {
      if (this.editingItem.quantity > 0 && this.editingItemIndex !== null) {
        this.form.items[this.editingItemIndex].quantity = this.editingItem.quantity
      }
      this.closeQuantityModal()
    },
    closeQuantityModal () { this.showQuantityModal = false },
    async openMembershipModal () {
      if (!this.selectedClient) {
        alert('Seleccione un cliente primero.')
        return
      }
      this.loadingMemberships = true
      this.showMembershipModal = true
      try {
        if (this.availableMemberships.length === 0) {
          this.availableMemberships = await ApiService.get('api/clientes/tipos-membresia')
        }
        try {
          const miembros = await ApiService.get(`api/clientes/${this.selectedClient.client_id}/miembros`)
          this.clientMembershipIds = Array.isArray(miembros)
            ? miembros.map(m => m.id_tipo_membresia)
            : []
        } catch (err) {
          this.clientMembershipIds = []
        }
      } catch (err) {
        console.error('Error al cargar membresías:', err)
        this.showMembershipModal = false
      } finally {
        this.loadingMemberships = false
      }
    },
    selectMembership (m) {
      this.form.items.push({
        id:       `memb_${m.id_tipo_membresia}`,
        name:     m.nombre_tipo,
        category: 'Membresía',
        price:    parseFloat(m.valor),
        quantity: 1,
        image:    '',
        has_vat:  false
      })
      this.showMembershipModal = false
    },
    handlePrint () {
      window.print()
    },
    handleCancel () {
      this.$router.push('/panel')
    },
    async handleSave () {
      if (!this.selectedClient) { alert('Seleccione un cliente.'); return }
      if (this.form.items.length === 0) { alert('Agregue al menos un artículo.'); return }

      this.savingDoc = true
      try {
        const payload = {
          cliente_id:      this.selectedClient.client_id,
          tipo_documento:  this.form.documentType,
          secuencia:       this.form.sequence,
          metodo_pago:     this.form.paymentMethod,
          caja:            this.assignedBox,
          vendedor:        this.form.seller,
          observaciones:   this.form.observations,
          descuento_pct:   this.discountPercentage,
          items: this.form.items.map(i => ({
            codigo:    i.id,
            nombre:    i.name,
            precio:    i.price,
            cantidad:  i.quantity,
            has_vat:   i.has_vat
          })),
          pagos: this.form.paymentMethod === 'contado'
            ? this.form.payments
            : [{ type: 'credito', amount: this.form.creditAmount, dias: this.form.creditDays }],
          subtotal_gravado:   this.subtotalGravado,
          subtotal_no_gravado: this.subtotalNoGravado,
          descuento:          this.descuento,
          iva:                this.tax,
          total:              this.total
        }
        await ApiService.post('api/panel/pagos/transacciones', payload)
        alert('Documento guardado correctamente.')
        this.$router.push('/panel')
      } catch (err) {
        console.error('Error al guardar documento:', err)
        const msg = err.response && err.response.data && err.response.data.mensaje
        alert('Error al guardar: ' + (msg || err.message))
      } finally {
        this.savingDoc = false
      }
    }
  }
}
</script>

<style scoped>
.ventas-form {
  background: var(--color-bg);
  min-height: 100vh;
  padding-bottom: 2rem;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Secciones */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
  margin: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.divider {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: 0;
}

/* Grids */
.header-grid,
.footer-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 768px) {
  .header-grid,
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
}

/* Inputs genéricos */
.form-input {
  flex: 1;
  min-width: 0;
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  color: var(--color-text-primary) !important;
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus { border-color: var(--color-primary) !important; }

.form-input.px { padding: 0.75rem; }

.form-input.input-readonly {
  background: var(--color-surface-2) !important;
  color: var(--color-text-muted) !important;
  cursor: not-allowed;
}

/* Barra de búsqueda con ícono */
.search-wrapper { position: relative; }

.input-row {
  display: flex;
  align-items: stretch;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: border-color 0.2s;
}

.input-row:focus-within { border-color: var(--color-primary); }

.input-row .form-input {
  border: none !important;
  border-radius: 0;
  background: transparent !important;
}

.input-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: color 0.2s;
}

.input-icon-btn:hover { color: var(--color-primary); }

/* Dropdown */
.dropdown-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 100;
  max-height: 240px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--color-border);
  transition: background 0.15s;
}

.dropdown-item:last-child { border-bottom: none; }
.dropdown-item:hover { background: var(--color-surface-2); }

.dropdown-main { font-weight: 500; color: var(--color-text-primary); font-size: 0.9rem; }
.dropdown-sub  { font-size: 0.78rem; color: var(--color-text-secondary); margin-top: 2px; }

/* Details row */
.details-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.select-wrapper { position: relative; flex: 1; }

.form-select {
  width: 100%;
  appearance: none;
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  color: var(--color-text-primary) !important;
  border-radius: var(--radius-md);
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  font-size: 0.95rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
}

.form-select:focus { border-color: var(--color-primary) !important; }

.select-icon {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--color-text-secondary);
  font-size: 20px;
}

/* Items list */
.items-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mb { margin-bottom: 0.75rem; }
.mt { margin-top: 0.75rem; }

.item-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--color-surface);
  padding: 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
}

.compact-card { padding: 0.5rem 0.75rem; }

.remove-badge {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-error);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  z-index: 1;
}

.remove-badge .material-symbols-outlined { font-size: 1rem; }

.item-thumb {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  flex-shrink: 0;
}

.item-info { flex: 1; min-width: 0; }
.item-name { font-size: 0.95rem; font-weight: 500; margin: 0; color: var(--color-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.item-cat  { font-size: 0.8rem; color: var(--color-text-secondary); margin: 0; }

.item-price-block { display: flex; flex-direction: column; align-items: flex-end; gap: 0.25rem; }
.item-price { font-size: 0.95rem; font-weight: 600; margin: 0; color: var(--color-primary); }

.item-qty {
  font-size: 0.75rem;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s;
}

.item-qty:hover { background: var(--color-border); }

.btn-add-membership {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.6rem;
  background: none;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-primary);
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-membership:hover {
  background: var(--color-primary-dim);
  border-color: var(--color-primary);
}

/* Payment tabs */
.payment-tabs {
  display: flex;
  background: var(--color-surface-2);
  border-radius: var(--radius-md);
  padding: 0.25rem;
  gap: 0.25rem;
}

.payment-tab {
  flex: 1;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--color-text-secondary);
  transition: all 0.2s;
}

.payment-tab:hover { color: var(--color-text-primary); }

.payment-tab-active {
  background: var(--color-primary);
  color: var(--color-bg);
}

/* Info box (crédito notice) */
.info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: var(--radius-md);
}

/* Form field */
.form-field { flex: 1; display: flex; flex-direction: column; gap: 0.375rem; }

.field-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Info cards (caja + vendedor) */
.info-cards-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.info-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.75rem;
}

.info-card-label { font-size: 0.75rem; color: var(--color-text-secondary); margin: 0 0 0.3rem; }
.info-card-content { display: flex; align-items: center; gap: 0.5rem; }
.info-card-value { font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); margin: 0; }

.info-select {
  background: none !important;
  border: none !important;
  color: var(--color-text-primary) !important;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  flex: 1;
  padding: 0;
}

/* Textarea */
.form-textarea {
  width: 100%;
  min-height: 90px;
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md);
  color: var(--color-text-primary) !important;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: none;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-textarea:focus { border-color: var(--color-primary) !important; }

/* Totales */
.totals-box {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
}

.total-label { font-size: 0.875rem; color: var(--color-text-secondary); display: flex; align-items: center; gap: 0.25rem; }
.total-value { font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); }

.discount-input {
  width: 3rem;
  background: var(--color-surface-2) !important;
  border: 1px solid var(--color-border) !important;
  color: var(--color-text-primary) !important;
  border-radius: 4px;
  padding: 0 0.25rem;
  text-align: right;
  font-size: 0.875rem;
  outline: none;
}

.total-divider {
  height: 1px;
  background: var(--color-border);
  margin: 0.75rem 0;
}

.total-final { margin-bottom: 0; font-size: 1.1rem; font-weight: 700; color: var(--color-text-primary); }
.total-amount { color: var(--color-primary); font-size: 1.5rem; font-weight: 700; }

/* Acciones */
.actions-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border);
}

.btn-icon-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-icon-action:hover { border-color: var(--color-primary); color: var(--color-primary); }

.btn-cancel {
  flex: 1;
  padding: 0.875rem 1rem;
  background: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover { border-color: var(--color-error); color: var(--color-error); }

.btn-save {
  flex: 2;
  padding: 0.875rem 1rem;
  background: var(--color-primary);
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-bg);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: var(--shadow-glow);
}

.btn-save:hover:not(:disabled) { transform: translateY(-2px); }
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

/* Icono pequeño */
.btn-icon-sm {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.25rem;
}

/* Modal internals */
.modal-field { display: flex; flex-direction: column; gap: 0.4rem; }

.membership-list {
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>



// WEBPACK FOOTER //
// src/components/Ventas/genera_venta.vue